import dayjs, { Dayjs } from 'dayjs';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { MITStatus } from '../store/modules/mit/types';
import { sanitizeNumericString } from '../util/helpers';
import { paylinkCustomerSchema } from '../store/modules/paylink/types';

const AmountSchema = z.string().refine(
  (amount) => {
    if (!amount) return true;
    const amountNumber = Number(sanitizeNumericString(amount as string));
    return !Number.isNaN(amountNumber) && amountNumber > 0 && amountNumber <= 2 ** 31 - 1;
  },
  {
    message: 'Invalid amount',
  },
);

const MIT_FILTER_STATUSES: readonly MITStatus[] = [
  MITStatus.PAID,
  MITStatus.FAILED,
  MITStatus.PARTIALLY_REFUNDED,
  MITStatus.REFUNDED,
];

const getValidationSchema = (maxDateRange: Props['maxDateRange'], requiredFields: string[]) => {
  const validationSchema = z
    .object({
      createdAfter: z.preprocess(
        (val) => (val ? dayjs(val as Dayjs).startOf('day') : null),
        z
          .instanceof(dayjs as unknown as typeof Dayjs)
          .optional()
          .nullish()
          .refine((val) => !requiredFields?.includes('createdAfter') || !!val, {
            message: 'Start date is required',
          }),
      ),
      createdBefore: z.preprocess(
        (val) => (val ? dayjs(val as Dayjs).endOf('day') : null),
        z
          .instanceof(dayjs as unknown as typeof Dayjs)
          .optional()
          .nullish()
          .refine((val) => !requiredFields?.includes('createdBefore') || !!val, {
            message: 'End date is required',
          }),
      ),
      amountGTE: AmountSchema.refine((val) => !requiredFields?.includes('amountGTE') || !!val, {
        message: 'Minimum amount is required',
      }),
      amountLTE: AmountSchema.refine((val) => !requiredFields?.includes('amountLTE') || !!val, {
        message: 'Maximum amount is required',
      }),
      status: z
        .array(z.enum(MIT_FILTER_STATUSES as readonly [string, ...string[]]))
        .refine((val) => !requiredFields?.includes('status') || val.length > 0, {
          message: 'At least one status is required',
        }),
      customers: z
        .array(paylinkCustomerSchema)
        .refine((val) => !requiredFields?.includes('customers') || val.length > 0, {
          message: 'At least one customer is required',
        }),
    })
    .superRefine((data, ctx) => {
      if (
        data.createdAfter &&
        data.createdBefore &&
        data.createdAfter.isAfter(data.createdBefore)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Start date must be before end date',
          path: ['createdAfter'],
        });
      }

      if (
        data.amountGTE &&
        data.amountLTE &&
        Number(sanitizeNumericString(data.amountGTE)) >
          Number(sanitizeNumericString(data.amountLTE))
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Minimum amount must be less than maximum amount',
          path: ['amountGTE'],
        });
      }

      if (maxDateRange && data.createdAfter && data.createdBefore) {
        const maxDateRangeAfter = dayjs(data.createdAfter)
          .add(...maxDateRange)
          .endOf('day');
        if (maxDateRangeAfter.isBefore(data.createdBefore)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Date range must be within ${maxDateRange[0]} ${maxDateRange[1]}s`,
            path: ['createdAfter'],
          });
        }
      }
    });

  return validationSchema;
};

type FormValues = z.infer<ReturnType<typeof getValidationSchema>>;

const emptyValues: FormValues = {
  createdAfter: null,
  createdBefore: null,
  amountGTE: '',
  amountLTE: '',
  status: [],
  customers: [],
};

interface Props {
  initialValues?: Partial<FormValues>;
  maxDateRange?: [number, dayjs.ManipulateType];
  requiredFields?: (keyof FormValues)[];
}

export const useMITFiltersForm = ({ initialValues, maxDateRange, requiredFields }: Props) => {
  return useForm<FormValues>({
    resolver: zodResolver(getValidationSchema(maxDateRange, requiredFields as string[])),
    defaultValues: { ...emptyValues, ...initialValues },
    reValidateMode: 'onBlur',
  });
};

export type { FormValues as MITFiltersFormValues };
export { MIT_FILTER_STATUSES, emptyValues as mitFiltersEmptyValues };
